import { getSupabaseBrowserClient } from '../lib/supabase'
import {
  nullableText,
  orderedReportPages,
  record,
  records,
  textValue,
  type ManagementStatement,
  type ReportPageCode,
  type ReportPreview,
  type ReportingAsset,
  type ReportingPayload,
  type ReportingSnapshot,
  type SnapshotListItem,
} from './reporting-model'

type RpcError = { message: string }
type RpcResponse = PromiseLike<{ data: unknown; error: RpcError | null }>
type RpcInvoker = (functionName: string, args?: Record<string, unknown>) => RpcResponse

export type AiRoadmapGrowthPoint = {
  quarterStart: string
  label: string
  totalInitiatives: number
  plannedInitiatives: number
  activeInitiatives: number | null
  quality: 'unavailable' | 'baseline' | 'partial' | 'complete'
}

export type AiRoadmapGrowth = {
  definition: 'initiative_with_pilot_or_production_ai_use'
  trackingStartedAt: string | null
  reportingTimeZone: string
  completeFrom: string | null
  windowStart: string | null
  windowEnd: string | null
  totalInitiatives: number
  timedInitiatives: number
  totalAiUses: number
  currentActiveAiUses: number
  points: AiRoadmapGrowthPoint[]
}

async function invoke(functionName: string, args: Record<string, unknown> = {}) {
  const client = getSupabaseBrowserClient() as unknown as { rpc: RpcInvoker }
  const { data, error } = await client.rpc(functionName, args)
  if (error) throw new Error(error.message)
  return data
}

function asset(value: unknown): ReportingAsset {
  const row = record(value)
  const pageCode = orderedReportPages([row.pageCode])[0]
  if (!pageCode) throw new Error('Reporting asset has an invalid page code')
  return {
    id: textValue(row.id),
    pageCode,
    storageObjectName: textValue(row.storageObjectName),
    fileName: textValue(row.fileName),
    mimeType: 'image/png',
    width: 1920,
    height: 1080,
    byteSize: Number(row.byteSize) || 0,
    sha256: textValue(row.sha256),
  }
}

function managementStatement(value: unknown): ManagementStatement | null {
  if (!value) return null
  const row = record(value)
  const statement = textValue(row.text)
  if (!statement) return null
  return {
    text: statement,
    authorUserId: textValue(row.authorUserId),
    authorName: textValue(row.authorName, 'Author not recorded'),
    recordedAt: textValue(row.recordedAt),
  }
}

function snapshot(value: unknown): ReportingSnapshot {
  const row = record(value)
  return {
    id: textValue(row.id),
    organisationId: textValue(row.organisationId),
    snapshotNo: Number(row.snapshotNo) || 0,
    snapshotAt: textValue(row.snapshotAt),
    reportingDate: textValue(row.reportingDate),
    pages: orderedReportPages(row.pages),
    templateVersion: textValue(row.templateVersion),
    metricsSchemaVersion: textValue(row.metricsSchemaVersion),
    strategyVersionId: nullableText(row.strategyVersionId),
    previousSnapshotId: nullableText(row.previousSnapshotId),
    payload: record(row.payload) as ReportingPayload,
    assetStatus: row.assetStatus === 'ready' ? 'ready' : 'pending',
    managementStatement: managementStatement(row.managementStatement),
    generatedBy: textValue(row.generatedBy),
    createdAt: textValue(row.createdAt),
    assets: records(row.assets).map(asset),
  }
}

export async function previewReportPack(organisationId: string, pages: ReportPageCode[]): Promise<ReportPreview> {
  const row = record(await invoke('preview_r1_report_pack', { p_organisation_id: organisationId, p_page_codes: pages }))
  return {
    preparedAt: textValue(row.preparedAt),
    pages: orderedReportPages(row.pages),
    payload: record(row.payload) as ReportingPayload,
    fingerprint: textValue(row.fingerprint),
  }
}

export async function getAiRoadmapGrowth(organisationId: string): Promise<AiRoadmapGrowth> {
  const row = record(await invoke('get_ai_roadmap_growth', { p_organisation_id: organisationId }))
  return {
    definition: 'initiative_with_pilot_or_production_ai_use',
    trackingStartedAt: nullableText(row.trackingStartedAt),
    reportingTimeZone: textValue(row.reportingTimeZone, 'UTC'),
    completeFrom: nullableText(row.completeFrom),
    windowStart: nullableText(row.windowStart),
    windowEnd: nullableText(row.windowEnd),
    totalInitiatives: Number(row.totalInitiatives) || 0,
    timedInitiatives: Number(row.timedInitiatives) || 0,
    totalAiUses: Number(row.totalAiUses) || 0,
    currentActiveAiUses: Number(row.currentActiveAiUses) || 0,
    points: records(row.points).map((point) => ({
      quarterStart: textValue(point.quarterStart),
      label: textValue(point.label),
      totalInitiatives: Number(point.totalInitiatives) || 0,
      plannedInitiatives: Number(point.plannedInitiatives) || 0,
      activeInitiatives: point.activeInitiatives == null ? null : Number(point.activeInitiatives) || 0,
      quality: ['baseline', 'partial', 'complete'].includes(textValue(point.quality))
        ? textValue(point.quality) as AiRoadmapGrowthPoint['quality']
        : 'unavailable',
    })),
  }
}

export async function generateReportPack(organisationId: string, preview: ReportPreview, statement: string): Promise<ReportingSnapshot> {
  return snapshot(await invoke('generate_r1_report_pack', {
    p_organisation_id: organisationId,
    p_page_codes: preview.pages,
    p_prepared_at: preview.preparedAt,
    p_fingerprint: preview.fingerprint,
    p_management_statement: statement.trim() || null,
  }))
}

export async function getReportingSnapshot(snapshotId: string): Promise<ReportingSnapshot> {
  return snapshot(await invoke('get_r1_reporting_snapshot', { p_snapshot_id: snapshotId }))
}

export async function listReportingSnapshots(organisationId: string): Promise<SnapshotListItem[]> {
  return records(await invoke('list_r1_reporting_snapshots', { p_organisation_id: organisationId })).map((row) => ({
    id: textValue(row.id),
    snapshotNo: Number(row.snapshotNo) || 0,
    snapshotAt: textValue(row.snapshotAt),
    reportingDate: textValue(row.reportingDate),
    pages: orderedReportPages(row.pages),
    assetStatus: row.assetStatus === 'ready' ? 'ready' : 'pending',
    managementStatementIncluded: row.managementStatementIncluded === true,
    generatedByName: textValue(row.generatedByName, 'Author not recorded'),
  }))
}

export async function uploadReportingAsset(snapshot: ReportingSnapshot, pageCode: ReportPageCode, fileName: string, blob: Blob, sha256: string) {
  const objectName = `${snapshot.organisationId}/${snapshot.id}/${fileName}`
  const client = getSupabaseBrowserClient()
  const { error } = await client.storage.from('board-reports').upload(objectName, blob, { contentType: 'image/png', upsert: false })
  if (error && !/already exists|duplicate/i.test(error.message)) throw new Error(error.message)
  await invoke('register_r1_reporting_asset', {
    p_snapshot_id: snapshot.id,
    p_page_code: pageCode,
    p_storage_object_name: objectName,
    p_file_name: fileName,
    p_byte_size: blob.size,
    p_sha256: sha256,
  })
}

export async function completeReportingAssets(snapshotId: string): Promise<ReportingSnapshot> {
  return snapshot(await invoke('complete_r1_reporting_snapshot_assets', { p_snapshot_id: snapshotId }))
}

export async function downloadReportingAsset(objectName: string): Promise<Blob> {
  const { data, error } = await getSupabaseBrowserClient().storage.from('board-reports').download(objectName)
  if (error) throw new Error(error.message)
  return data
}

export async function recordReportingDownload(snapshotId: string, type: 'png' | 'zip') {
  await invoke('record_r1_reporting_download', { p_snapshot_id: snapshotId, p_download_type: type })
}
